import React from "react";
import styled from "styled-components";
import Window from "../Window";
import MsgIcon from "../MsgIcon";
import { modalDing } from "../../sound/sound";

// THE Win95 message box — every modal dialog in the piece renders
// through this one component. Anatomy: title bar, 32px icon at the
// left of left-aligned text, centered button row. The scrim is part
// of the component: clicking outside the box answers with a ding +
// title-bar flash rather than doing nothing (Win95's "this dialog
// owns you" gesture).
//
// Keyboard (whole-window listeners; modalGate silences everything
// else while mounted): Enter = default (first) button, Escape =
// onCancel when given, Tab cycles the buttons.
//
// Button guard: clicks are ignored for a beat after mount, so a click
// aimed at a dialog this one replaced in place can't land on this
// one's button. Remount-per-content is the caller's job (key on the
// dialog id).

const FLASH_TOGGLES = 6; // 3 off/on cycles of the title bar
const FLASH_MS = 60;

function MessageBox({ title, icon = "question", buttons, onCancel, children }) {
  const [armed, setArmed] = React.useState(false);
  React.useEffect(() => {
    const t = setTimeout(() => setArmed(true), 350);
    return () => clearTimeout(t);
  }, []);

  // Title-bar flash: rapid active/inactive toggling, counted down by an
  // interval. Restarted (not stacked) if they keep clicking outside.
  const [flashLeft, setFlashLeft] = React.useState(0);
  React.useEffect(() => {
    if (flashLeft <= 0) return undefined;
    const t = setTimeout(() => setFlashLeft((n) => n - 1), FLASH_MS);
    return () => clearTimeout(t);
  }, [flashLeft]);
  const rebuff = () => {
    modalDing();
    setFlashLeft(FLASH_TOGGLES);
  };

  const rootRef = React.useRef(null);
  React.useEffect(() => {
    // Focus lands on the default button; Tab cycles inside the dialog.
    const root = rootRef.current;
    if (!root) return undefined;
    const buttonEls = () => Array.from(root.querySelectorAll("button"));
    buttonEls()[0]?.focus();
    const onKey = (e) => {
      if (e.key === "Tab") {
        const els = buttonEls();
        if (els.length === 0) return;
        e.preventDefault();
        const i = els.indexOf(document.activeElement);
        const next =
          (i + (e.shiftKey ? -1 : 1) + els.length) % els.length;
        els[next].focus();
      } else if (e.key === "Enter") {
        e.preventDefault();
        const el = document.activeElement;
        if (el && buttonEls().includes(el)) el.click();
        else buttonEls()[0]?.click();
      } else if (e.key === "Escape" && onCancel) {
        e.preventDefault();
        onCancel();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Scrim onMouseDown={rebuff}>
      <BoxWrap
        ref={rootRef}
        onMouseDown={(e) => e.stopPropagation()}
        data-flash-dim={flashLeft % 2 === 1 ? "true" : undefined}
      >
        <Window title={title} inactive={flashLeft % 2 === 1}>
          <Body>
            <Message>
              <MsgIcon kind={icon} size={32} />
              {/* div, not p: rich bodies can contain block content,
                  and <div>-in-<p> is invalid HTML. */}
              <MessageText>{children}</MessageText>
            </Message>
            <Buttons>
              {buttons.map((b) => (
                <button
                  key={b.label}
                  type="button"
                  onClick={armed ? b.onClick : undefined}
                >
                  {b.label}
                </button>
              ))}
            </Buttons>
          </Body>
        </Window>
      </BoxWrap>
    </Scrim>
  );
}

// Truly modal: the scrim catches every pointer event, so nothing behind
// the dialog is interactable while it's up — and it answers the click.
const Scrim = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const BoxWrap = styled.div`
  min-width: 260px;
  max-width: 400px;
`;

const Body = styled.div`
  padding: 14px 18px 12px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Message = styled.div`
  display: flex;
  /* Icon rides the top-left like a real Win9x message box — stays
     visible on tall bodies. */
  align-items: flex-start;
  gap: 14px;
  text-align: left;

  & p {
    margin: 0;
  }
`;

const MessageText = styled.div`
  margin: 0;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;

  & button {
    min-width: 74px;
  }
`;

export default MessageBox;
